import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  BookOpen, Calendar, Check, ChevronDown, ChevronUp, Clock, Copy, Download, Edit3, Eye, FileText,
  Film, GraduationCap, Heart, Loader, Megaphone, Mic, Newspaper, Palette, PenTool, Play, Plus,
  RefreshCw, Save, Search, Sparkles, Trash2, Users, X, Zap,
} from 'lucide-react';
import { Icon } from './Icon';
import { useConfirm } from '../confirmContext';
import LoadFailure from './LoadFailure';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import Tabs from './ui/Tabs';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'biography', label: 'Biography' },
  { id: 'family', label: 'Family' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'educational', label: 'Educational' },
  { id: 'memorial', label: 'Memorial' },
  { id: 'podcast', label: 'Podcast' },
  { id: 'creative', label: 'Creative' },
  { id: 'documentation', label: 'Documentation' },
  { id: 'custom', label: 'Custom' },
];

const _ts = () => { const d = new Date(); return `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}_${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}${String(d.getSeconds()).padStart(2,'0')}`; };

export default function PromptLibraryPanel({ theme, isDarkMode, selectedVideo, subjectName, getProjectRoot, onBack, BACKEND_URL, pushToast = () => {}, axiosAuth, logActivity = () => {} }) {
  const confirm = useConfirm();
  // Use axiosAuth if available (sends Bearer token + has baseURL), otherwise fall back to plain axios
  const api = axiosAuth || axios;
  const B = axiosAuth ? '' : BACKEND_URL; // axiosAuth already has baseURL baked in
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinct from "loaded and empty" — see the render guard.
  const [promptsError, setPromptsError] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // prompt object or { new: true }
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [genParams, setGenParams] = useState({});

  const root = getProjectRoot ? getProjectRoot(selectedVideo || '') : '';
  const accent = theme.glossaryAccent || '#6366f1';

  // Past generations
  const [generations, setGenerations] = useState([]);
  const [gensLoading, setGensLoading] = useState(false);
  const [gensError, setGensError] = useState(false);
  const [viewingGen, setViewingGen] = useState(null); // full generation object being viewed
  const [activeView, setActiveView] = useState('prompts'); // 'prompts' | 'generated' | 'styles'
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [customStyles, setCustomStyles] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gwv_prompt_styles') || '[]'); } catch { return []; }
  });
  const [editingStyle, setEditingStyle] = useState(null);

  const BUILT_IN_STYLES = [
    { id: 'academic', name: 'Academic', lucideIcon: GraduationCap, color: '#3b82f6',
      description: 'Formal scholarly tone with citations and analytical framing',
      system_prompt: 'Write in an academic, scholarly style. Use formal language, analytical framing, and cite evidence from the testimony. Structure with clear sections and maintain objectivity while honoring the subject\'s voice.' },
    { id: 'documentary', name: 'Documentary', lucideIcon: Film, color: '#ef4444',
      description: 'Cinematic narration as if for a documentary film',
      system_prompt: 'Write as if narrating a documentary film. Use vivid, cinematic language that draws the viewer in. Begin with a compelling hook. Weave factual details into a flowing narrative arc. Balance emotional resonance with historical accuracy.' },
    { id: 'conversational', name: 'Conversational', lucideIcon: Megaphone, color: '#22c55e',
      description: 'Warm, approachable tone as if telling the story to a friend',
      system_prompt: 'Write in a warm, conversational tone as if sharing someone\'s story with a friend. Use accessible language, personal observations, and let the subject\'s own words shine through. Avoid academic jargon.' },
    { id: 'educational', name: 'Educational', lucideIcon: BookOpen, color: '#f59e0b',
      description: 'Structured for teaching, with context and key takeaways',
      system_prompt: 'Write for an educational audience. Provide historical context, explain significance of events, include key dates and facts. Structure content with clear headings. Add discussion questions or reflection prompts where appropriate.' },
    { id: 'memorial', name: 'Memorial', lucideIcon: Heart, color: '#8b5cf6',
      description: 'Reverent, dignified tribute honoring the life described',
      system_prompt: 'Write as a memorial tribute. Use reverent, dignified language that honors the person\'s life and legacy. Focus on resilience, humanity, and what they want remembered. Include both sorrow and hope. This is sacred testimony.' },
    { id: 'journalistic', name: 'Journalistic', lucideIcon: Newspaper, color: '#6b7280',
      description: 'Factual reporting style with who/what/when/where/why',
      system_prompt: 'Write in a journalistic style. Lead with the most compelling fact. Use the inverted pyramid structure. Include direct quotes from the testimony. Verify details against the timeline. Maintain balanced, factual reporting.' },
    { id: 'podcast', name: 'Podcast Script', lucideIcon: Mic, color: '#ec4899',
      description: 'Scripted for audio narration with natural pauses and emphasis',
      system_prompt: 'Write as a podcast script. Use natural spoken cadence with [pause] markers. Include host transitions and reflections. Break long passages into digestible segments. Use direct quotes liberally — let the subject tell their story in their own words.' },
    { id: 'children', name: 'Young Readers', lucideIcon: Users, color: '#0ea5e9',
      description: 'Age-appropriate for younger audiences (12+), sensitive but honest',
      system_prompt: 'Write for young readers aged 12-16. Use clear, age-appropriate language. Explain historical context simply. Be honest about difficult events without graphic detail. Focus on themes of courage, family, and resilience. Include a glossary of historical terms.' },
    { id: 'first_person', name: 'First Person', lucideIcon: Mic, color: '#d97706',
      description: 'Reconstructed first-person narrative based on testimony',
      system_prompt: 'Write as a first-person narrative reconstruction based on the testimony. Use "I" voice as if the subject is telling their own story. Stay faithful to their actual words and experiences. Include their emotional texture and personal observations as expressed in the interview.' },
    { id: 'timeline', name: 'Chronological', lucideIcon: Calendar, color: '#14b8a6',
      description: 'Strict chronological account organized by date and period',
      system_prompt: 'Write in strict chronological order. Organize by time periods (pre-war, occupation, camp, liberation, post-war). Include specific dates when known. Each section should note the subject\'s age at the time. Use a factual, structured approach.' },
  ];

  const allStyles = [...BUILT_IN_STYLES, ...customStyles];

  const saveCustomStyles = (styles) => {
    setCustomStyles(styles);
    localStorage.setItem('gwv_prompt_styles', JSON.stringify(styles));
  };

  const applyStyleToPrompt = (style) => {
    if (!editing) return;
    setEditing(prev => ({ ...prev, system_prompt: style.system_prompt }));
    setActiveView('prompts');
    if (pushToast) pushToast(`style-${Date.now()}`, 'success', `Applied "${style.name}" style`);
  };

  const loadPrompts = useCallback(() => {
    setLoading(true);
    api.get(`${B}/prompts`)
      .then(r => { setPrompts(r.data || []); setPromptsError(false); })
      .catch(() => setPromptsError(true))
      .finally(() => setLoading(false));
  }, [BACKEND_URL]);

  useEffect(() => { loadPrompts(); }, [loadPrompts]);

  const loadGenerations = useCallback(() => {
    if (!root) { setGenerations([]); setGensError(false); return; }
    setGensLoading(true);
    api.get(`${B}/prompts/generations/${encodeURIComponent(root)}`)
      .then(r => { setGenerations(r.data || []); setGensError(false); })
      .catch(() => setGensError(true))
      .finally(() => setGensLoading(false));
  }, [BACKEND_URL, root, api]);

  useEffect(() => { loadGenerations(); }, [loadGenerations]);

  const savePrompt = async (data) => {
    try {
      if (data.id) {
        await api.put(`${B}/prompts/${data.id}`, data);
      } else {
        await api.post(`${B}/prompts`, data);
      }
      setEditing(null);
      loadPrompts();
    } catch (err) { pushToast('prompt-save-error', 'error', 'Save failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const deletePrompt = async (id) => {
    if (!await confirm({ message: 'Delete this prompt?', title: 'Delete prompt?', confirmLabel: 'Delete', destructive: true })) return;
    await api.delete(`${B}/prompts/${id}`);
    loadPrompts();
  };

  const seedPrompts = async () => {
    const res = await api.post(`${B}/prompts/seed`);
    if (res.data.status === 'skipped') pushToast('prompt-seed-skipped', 'info', res.data.message);
    else loadPrompts();
  };

  // Poll timer for an in-flight generation — kept in a ref so it can be cleared on unmount
  const genPollRef = React.useRef(null);
  useEffect(() => () => { if (genPollRef.current) clearInterval(genPollRef.current); }, []);

  const runGenerate = async () => {
    if (!selectedPrompt || !root) return;
    if (genPollRef.current) clearInterval(genPollRef.current);
    setGenerating(true);
    setGenResult(null);
    const toastId = `prompt-gen-${Date.now()}`;
    const actLabel = `Prompt: ${selectedPrompt.name}`;
    pushToast(toastId, 'running', `Generating "${selectedPrompt.name}"...`);
    logActivity(toastId, 'prompt', actLabel, 'running');
    try {
      const res = await api.post(`${B}/prompts/${selectedPrompt.id}/generate`, {
        project_root: root,
        params: genParams,
      });
      const jobId = res.data.job_id;
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const jr = await api.get(`${B}/jobs/${jobId}`);
          if (jr.data.status === 'complete') {
            clearInterval(poll); genPollRef.current = null;
            setGenResult(jr.data.result);
            setGenerating(false);
            loadGenerations();
            const tokens = jr.data.result?.tokens || 0;
            const cost = jr.data.result?.cost_usd?.toFixed(4) || '0';
            pushToast(toastId, 'success', `Generated "${selectedPrompt.name}" — ${tokens} tokens, $${cost}`);
            logActivity(toastId, 'prompt', `${actLabel} — ${tokens}t, $${cost}`, 'success');
          } else if (jr.data.status === 'failed') {
            clearInterval(poll); genPollRef.current = null;
            setGenerating(false);
            const errMsg = jr.data.error || 'Unknown error';
            pushToast(toastId, 'error', `Generation failed: ${errMsg}`);
            logActivity(toastId, 'prompt', actLabel, 'error', { message: errMsg });
          }
        } catch { /* keep polling */ }
      }, 2000);
      genPollRef.current = poll;
    } catch (err) {
      setGenerating(false);
      const errMsg = err.response?.data?.detail || err.message;
      pushToast(toastId, 'error', `Generation failed: ${errMsg}`);
      logActivity(toastId, 'prompt', actLabel, 'error', { message: errMsg });
    }
  };

  const filtered = prompts.filter(p => {
    if (filter !== 'all' && p.category !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const copyOutput = () => {
    if (genResult?.output) navigator.clipboard.writeText(genResult.output);
  };

  const downloadOutput = () => {
    if (!genResult?.output) return;
    const blob = new Blob([genResult.output], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(subjectName || root || 'output').replace(/\s+/g, '_')}_${selectedPrompt?.name?.replace(/\s+/g, '_') || 'prompt'}_${_ts()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Extract {{param}} placeholders from prompt (excluding data placeholders)
  const DATA_PLACEHOLDERS = new Set(['subject_name', 'born', 'birthplace', 'death_year', 'death_place', 'biography', 'religion', 'family', 'timeline', 'places', 'available_languages', 'pronunciation', 'transcript_en']);
  const extractParams = (text) => {
    const matches = text.match(/\{\{(\w+)\}\}/g) || [];
    return [...new Set(matches.map(m => m.slice(2, -2)))].filter(k => !DATA_PLACEHOLDERS.has(k));
  };

  const promptTextareaRef = React.useRef(null);
  const insertPlaceholder = (key) => {
    const tag = `{{${key}}}`;
    const ta = promptTextareaRef.current;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const text = editing.user_prompt || '';
      const newText = text.slice(0, start) + tag + text.slice(end);
      setEditing(prev => ({ ...prev, user_prompt: newText }));
      setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + tag.length; }, 0);
    } else {
      setEditing(prev => ({ ...prev, user_prompt: (prev.user_prompt || '') + tag }));
    }
  };

  const PLACEHOLDER_PILLS = [
    { key: 'subject_name', label: 'Subject Name', color: '#3b82f6' },
    { key: 'biography', label: 'Biography', color: '#8b5cf6' },
    { key: 'family', label: 'Family Tree', color: '#f59e0b' },
    { key: 'timeline', label: 'Timeline', color: '#22c55e' },
    { key: 'places', label: 'Places', color: '#ec4899' },
    { key: 'born', label: 'Born', color: '#6b7280' },
    { key: 'birthplace', label: 'Birthplace', color: '#6b7280' },
    { key: 'death_year', label: 'Death Year', color: '#6b7280' },
    { key: 'death_place', label: 'Death Place', color: '#6b7280' },
    { key: 'religion', label: 'Religion', color: '#6b7280' },
    { key: 'available_languages', label: 'Languages', color: '#0ea5e9' },
    { key: 'pronunciation', label: 'Pronunciation Guide', color: '#d97706' },
    { key: 'transcript_en', label: 'Transcript (EN)', color: '#d97706' },
  ];

  const inputStyle = { width: '100%', padding: '7px 10px', background: theme.inputBg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '7px', outline: 'none', boxSizing: 'border-box', fontSize: '0.72rem' };

  return (
    <div style={{ display: 'flex', height: '100%', width: '100%', background: theme.bg, overflow: 'hidden' }}>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', flexDirection: 'column', flex: '0 1 1200px', overflow: 'hidden' }}>

        {/* Tab bar */}
        <div style={{ display: 'flex', justifyContent: 'center', background: theme.bg, flexShrink: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 8px 7px', minHeight: '40px', boxSizing: 'border-box',
            background: theme.sidebar,
            border: `1px solid ${theme.border}`, borderTop: 'none',
            borderRadius: '0 0 14px 14px',
            boxShadow: isDarkMode ? '0 8px 18px rgba(0,0,0,0.18)' : '0 8px 18px rgba(15,23,42,0.08)',
          }}>
            <Tabs
              theme={theme}
              fill={false}
              activeColor={accent}
              value={activeView}
              onChange={(k) => { setActiveView(k); if (k === 'generated') loadGenerations(); }}
              tabs={[
                { key: 'prompts', label: 'PROMPTS', icon: BookOpen },
                { key: 'generated', label: 'GENERATED', icon: FileText, count: generations.length || undefined },
                { key: 'styles', label: 'STYLES', icon: Palette },
              ]}
              style={{ background: 'transparent', padding: 0 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {activeView === 'styles' ? (
            /* ═══════ STYLES VIEW ═══════ */
            <>
              {/* LEFT — Style list */}
              <div style={{ width: '360px', flexShrink: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px', padding: '0 4px' }}>
                  Built-in Styles
                </div>
                {[...BUILT_IN_STYLES].sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                  <div key={s.id} onClick={() => setSelectedStyle(s)}
                    style={{
                      padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                      background: selectedStyle?.id === s.id ? `${s.color}15` : theme.card,
                      border: `1px solid ${selectedStyle?.id === s.id ? s.color : theme.border}`,
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                      {s.lucideIcon ? <Icon icon={s.lucideIcon} size={16} color={s.color} /> : <span style={{ fontSize: '1rem' }}>{s.icon || '✏️'}</span>}
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.text }}>{s.name}</span>
                    </div>
                    <div style={{ fontSize: '0.62rem', color: theme.textSub, lineHeight: 1.4 }}>{s.description}</div>
                  </div>
                ))}

                {customStyles.length > 0 && (
                  <>
                    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '8px', marginBottom: '4px', padding: '0 4px' }}>
                      Custom Styles
                    </div>
                    {customStyles.map(s => (
                      <div key={s.id} onClick={() => setSelectedStyle(s)}
                        style={{
                          padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                          background: selectedStyle?.id === s.id ? `${accent}15` : theme.card,
                          border: `1px solid ${selectedStyle?.id === s.id ? accent : theme.border}`,
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {s.lucideIcon ? <Icon icon={s.lucideIcon} size={16} color={accent} /> : <span style={{ fontSize: '1rem' }}>{s.icon || '✏️'}</span>}
                          <span style={{ flex: 1, fontSize: '0.75rem', fontWeight: 700, color: theme.text }}>{s.name}</span>
                          <Button theme={theme} variant="danger-ghost" size="sm" icon={Trash2}
                            onClick={async e => { e.stopPropagation(); if (!await confirm({ message: `Delete the "${s.name}" style? This cannot be undone.`, title: 'Delete style?', confirmLabel: 'Delete', destructive: true })) return; saveCustomStyles(customStyles.filter(c => c.id !== s.id)); if (selectedStyle?.id === s.id) setSelectedStyle(null); }}
                            title="Delete" ariaLabel={`Delete ${s.name} style`} />
                        </div>
                        <div style={{ fontSize: '0.62rem', color: theme.textSub, marginTop: '2px' }}>{s.description}</div>
                      </div>
                    ))}
                  </>
                )}

                <button onClick={() => setEditingStyle({ id: `custom-${Date.now()}`, name: '', description: '', icon: '✏️', system_prompt: '' })}
                  style={{ padding: '8px', background: 'none', border: `1px dashed ${theme.border}`, borderRadius: '7px', cursor: 'pointer', color: theme.textSub, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontSize: '0.68rem', fontWeight: 700, marginTop: '4px' }}>
                  <Icon icon={Plus} size={11} /> New Custom Style
                </button>
              </div>

              {/* Divider */}
              <div style={{ width: '1px', background: theme.border, margin: '20px 0', flexShrink: 0 }} />

              {/* RIGHT — Style detail / editor */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '10px 14px', overflow: 'auto' }}>
                {editingStyle ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: theme.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon icon={PenTool} size={14} /> {editingStyle.name ? 'Edit Style' : 'New Custom Style'}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Input theme={theme} fullWidth={false} value={editingStyle.icon || ''} onChange={e => setEditingStyle(prev => ({ ...prev, icon: e.target.value }))} placeholder="Emoji" maxLength={4}
                        style={{ width: '50px', textAlign: 'center', fontSize: '1.1rem' }} />
                      <Input theme={theme} size="sm" fullWidth={false} value={editingStyle.name || ''} onChange={e => setEditingStyle(prev => ({ ...prev, name: e.target.value }))} placeholder="Style name"
                        style={{ flex: 1 }} />
                    </div>
                    <Input theme={theme} size="sm" value={editingStyle.description || ''} onChange={e => setEditingStyle(prev => ({ ...prev, description: e.target.value }))} placeholder="Short description of this writing style" />
                    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase' }}>System Prompt (Writing Instructions)</div>
                    <Input theme={theme} multiline size="sm" value={editingStyle.system_prompt || ''} onChange={e => setEditingStyle(prev => ({ ...prev, system_prompt: e.target.value }))} rows={10}
                      placeholder="Describe the writing style, tone, structure, and approach..."
                      style={{ minHeight: '200px', lineHeight: 1.6 }} />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => {
                        if (!editingStyle.name?.trim() || !editingStyle.system_prompt?.trim()) return;
                        const exists = customStyles.find(c => c.id === editingStyle.id);
                        if (exists) {
                          saveCustomStyles(customStyles.map(c => c.id === editingStyle.id ? editingStyle : c));
                        } else {
                          saveCustomStyles([...customStyles, editingStyle]);
                        }
                        setSelectedStyle(editingStyle);
                        setEditingStyle(null);
                      }}
                        style={{ flex: 1, padding: '9px', background: accent, color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, cursor: 'pointer', fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                        <Icon icon={Save} size={13} /> Save Style
                      </button>
                      <Button theme={theme} variant="secondary" onClick={() => setEditingStyle(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : selectedStyle ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {selectedStyle.lucideIcon
                        ? <Icon icon={selectedStyle.lucideIcon} size={28} color={selectedStyle.color} />
                        : <span style={{ fontSize: '1.6rem' }}>{selectedStyle.icon || '✏️'}</span>}
                      <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: theme.text }}>{selectedStyle.name}</div>
                        <div style={{ fontSize: '0.68rem', color: theme.textSub }}>{selectedStyle.description}</div>
                      </div>
                    </div>

                    <div style={{ background: theme.card, borderRadius: '10px', border: `1px solid ${theme.border}`, padding: '14px' }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.05em' }}>System Prompt</div>
                      <div style={{ fontSize: '0.75rem', color: theme.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{selectedStyle.system_prompt}</div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => applyStyleToPrompt(selectedStyle)} disabled={!editing}
                        style={{ flex: 1, padding: '10px', background: editing ? accent : theme.card, color: editing ? '#fff' : theme.textSub, border: 'none', borderRadius: '8px', fontWeight: 700, cursor: editing ? 'pointer' : 'default', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: editing ? 1 : 0.5 }}>
                        <Icon icon={Zap} size={13} /> {editing ? 'Apply to Current Prompt' : 'Edit a prompt first to apply'}
                      </button>
                      <Button theme={theme} variant="secondary" icon={Copy} onClick={() => {
                        if (selectedStyle.system_prompt) navigator.clipboard.writeText(selectedStyle.system_prompt);
                        if (pushToast) pushToast(`copy-${Date.now()}`, 'success', 'Copied to clipboard');
                      }}>
                        Copy
                      </Button>
                      {customStyles.find(c => c.id === selectedStyle.id) && (
                        <Button theme={theme} variant="secondary" icon={Edit3} onClick={() => setEditingStyle({ ...selectedStyle })}>
                          Edit
                        </Button>
                      )}
                    </div>

                    {!editing && (
                      <div style={{ fontSize: '0.62rem', color: theme.textSub, background: `${accent}08`, border: `1px dashed ${accent}30`, borderRadius: '8px', padding: '10px', lineHeight: 1.5 }}>
                        💡 To apply a style: Go to the <strong>Prompts</strong> tab, click <strong>Edit</strong> on a prompt (or create a new one), then come back here and click <strong>Apply to Current Prompt</strong>. The style will be set as the system prompt.
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: '10px', color: theme.textSub }}>
                    <Icon icon={Palette} size={36} />
                    <div style={{ fontSize: '0.78rem' }}>Select a writing style to preview</div>
                    <div style={{ fontSize: '0.65rem', maxWidth: '300px', textAlign: 'center', lineHeight: 1.5 }}>
                      Styles control the tone and structure of generated content. Apply a style to set the system prompt for any prompt template.
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : activeView === 'generated' ? (
            /* ═══════ GENERATED FILES VIEW ═══════ */
            <>
              {/* LEFT — Generation list */}
              <div style={{ width: '360px', flexShrink: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {gensLoading ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub }}><Icon icon={Loader} size={16} /> Loading...</div>
                ) : gensError ? (
                  <LoadFailure theme={theme} what="past generations" onRetry={loadGenerations} compact />
                ) : generations.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub, fontSize: '0.72rem' }}>
                    No generated files yet. Select a prompt and generate content first.
                  </div>
                ) : generations.map(g => {
                  const isActive = viewingGen?.id === g.id;
                  const created = g.created_at ? new Date(g.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                  return (
                    <div key={g.id} onClick={() => setViewingGen(isActive ? null : g)}
                      style={{
                        padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                        background: isActive ? `${accent}15` : theme.card,
                        border: `1px solid ${isActive ? accent : theme.border}`,
                      }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.text, marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.prompt_name || 'Untitled'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.52rem', fontWeight: 700, color: accent, textTransform: 'uppercase', background: `${accent}12`, padding: '1px 5px', borderRadius: '3px' }}>{g.prompt_category || 'custom'}</span>
                        <span style={{ fontSize: '0.52rem', color: theme.textSub, display: 'flex', alignItems: 'center', gap: '2px' }}><Icon icon={Clock} size={8} /> {created}</span>
                        <span style={{ fontSize: '0.52rem', color: theme.textSub }}>{g.tokens_used || 0}t · ${(g.cost_usd || 0).toFixed(4)}</span>
                        <span style={{ fontSize: '0.52rem', color: theme.textSub }}>{g.model_used}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Divider */}
              <div style={{ width: '1px', background: theme.border, margin: '20px 0', flexShrink: 0 }} />

              {/* RIGHT — Generated content viewer */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '10px 14px', overflow: 'hidden' }}>
                {viewingGen ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexShrink: 0 }}>
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: theme.text }}>{viewingGen.prompt_name || 'Untitled'}</div>
                        <div style={{ fontSize: '0.6rem', color: theme.textSub, marginTop: '2px' }}>
                          {viewingGen.created_at ? new Date(viewingGen.created_at).toLocaleString() : ''} · {viewingGen.model_used} · {viewingGen.created_by || 'unknown'} · {viewingGen.tokens_used || 0} tokens · ${(viewingGen.cost_usd || 0).toFixed(4)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <Button theme={theme} variant="secondary" size="sm" icon={Copy}
                          onClick={() => { if (viewingGen.output_text) navigator.clipboard.writeText(viewingGen.output_text); }}>
                          Copy
                        </Button>
                        <Button theme={theme} variant="secondary" size="sm" icon={Download} onClick={() => {
                          if (!viewingGen.output_text) return;
                          const blob = new Blob([viewingGen.output_text], { type: 'text/markdown' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${(viewingGen.prompt_name || 'output').replace(/\s+/g, '_')}_${(viewingGen.created_at || '').slice(0, 10)}_${_ts()}.md`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}>
                          Download .md
                        </Button>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', fontSize: '0.78rem', lineHeight: 1.7, color: theme.text, whiteSpace: 'pre-wrap', fontFamily: "'Georgia', serif", background: theme.card, borderRadius: '10px', padding: '16px', border: `1px solid ${theme.border}` }}>
                      {viewingGen.output_text}
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: '10px', color: theme.textSub }}>
                    <Icon icon={FileText} size={36} />
                    <div style={{ fontSize: '0.78rem' }}>Select a generated file to view its content</div>
                  </div>
                )}
              </div>
            </>
          ) : (
          /* ═══════ PROMPTS VIEW ═══════ */
          <>
          {/* LEFT — Library list */}
          <div style={{ width: '360px', flexShrink: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>

            {/* Search + seed */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <Icon icon={Search} size={12} color={theme.textSub} style={{ position: 'absolute', left: '8px', top: '9px' }} />
                <Input theme={theme} size="sm" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search prompts..."
                  style={{ paddingLeft: '28px' }} />
              </div>
              <Button theme={theme} variant="secondary" size="sm" icon={Sparkles} onClick={seedPrompts} title="Seed starter prompts">
                Seed
              </Button>
            </div>

            {/* Category filter */}
            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
              {[CATEGORIES[0], ...CATEGORIES.slice(1).sort((a, b) => a.label.localeCompare(b.label))].map(c => (
                <button key={c.id} onClick={() => setFilter(c.id)}
                  style={{ padding: '2px 8px', fontSize: '0.58rem', fontWeight: 600, borderRadius: '4px', cursor: 'pointer',
                    background: filter === c.id ? accent : theme.card, color: filter === c.id ? '#fff' : theme.textSub,
                    border: `1px solid ${filter === c.id ? accent : theme.border}` }}>
                  {c.label}
                </button>
              ))}
            </div>

            {/* New prompt button */}
            <button onClick={() => setEditing({ name: '', description: '', category: 'custom', model: 'claude', system_prompt: '', user_prompt: '', output_format: 'markdown', is_shared: true })}
              style={{ padding: '6px', background: 'none', border: `1px dashed ${theme.border}`, borderRadius: '7px', cursor: 'pointer', color: theme.textSub, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontSize: '0.68rem', fontWeight: 700 }}>
              <Icon icon={Plus} size={11} /> New Prompt
            </button>

            {/* Prompt cards */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub }}><Icon icon={Loader} size={16} /> Loading...</div>
            ) : promptsError ? (
              /* Must come before the empty state. When the fetch failed, the
                 old code showed "No prompts found. Click Seed to add starter
                 prompts" — inviting the user to write starter data over a
                 library that may be perfectly intact and merely unreachable. */
              <LoadFailure theme={theme} what="the prompt library" onRetry={loadPrompts} />
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub, fontSize: '0.72rem' }}>
                No prompts found. Click "Seed" to add starter prompts.
              </div>
            ) : filtered.map(p => (
              <div key={p.id} onClick={() => { setSelectedPrompt(p); setGenResult(null); setGenParams({}); }}
                style={{
                  padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                  background: selectedPrompt?.id === p.id ? `${accent}15` : theme.card,
                  border: `1px solid ${selectedPrompt?.id === p.id ? accent : theme.border}`,
                  transition: '0.1s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.text }}>{p.name}</span>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    <Button theme={theme} variant="ghost" size="sm" icon={Edit3} onClick={e => { e.stopPropagation(); setEditing(p); }} title="Edit" />
                    <Button theme={theme} variant="danger-ghost" size="sm" icon={Trash2} onClick={e => { e.stopPropagation(); deletePrompt(p.id); }} title="Delete" />
                  </div>
                </div>
                <div style={{ fontSize: '0.62rem', color: theme.textSub, marginBottom: '3px' }}>{p.description}</div>
                <span style={{ fontSize: '0.52rem', fontWeight: 700, color: accent, textTransform: 'uppercase', background: `${accent}12`, padding: '1px 5px', borderRadius: '3px' }}>{p.category}</span>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div style={{ width: '1px', background: theme.border, margin: '20px 0', flexShrink: 0 }} />

          {/* RIGHT — Generate / Edit / Output */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '10px 14px', gap: '10px', overflow: 'auto' }}>

            {editing ? (
              /* ── Edit mode ── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon icon={Edit3} size={13} /> {editing.id ? 'Edit Prompt' : 'New Prompt'}
                </div>
                <Input theme={theme} size="sm" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="Prompt name" />
                <Input theme={theme} size="sm" value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} placeholder="Description" />
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Select theme={theme} size="sm" fullWidth={false} value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} style={{ flex: 1 }}>
                    {CATEGORIES.filter(c => c.id !== 'all').map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </Select>
                  <Select theme={theme} size="sm" fullWidth={false} value={editing.model} onChange={e => setEditing({ ...editing, model: e.target.value })} style={{ flex: 1 }}>
                    <option value="claude">Claude</option>
                    <option value="gemini">Gemini</option>
                  </Select>
                </div>
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase' }}>System Prompt (optional)</div>
                <Input theme={theme} multiline size="sm" value={editing.system_prompt} onChange={e => setEditing({ ...editing, system_prompt: e.target.value })} rows={3} placeholder="Optional system instructions..." style={{ minHeight: '60px' }} />
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase' }}>User Prompt Template</div>
                <textarea ref={promptTextareaRef} value={editing.user_prompt} onChange={e => setEditing({ ...editing, user_prompt: e.target.value })} rows={12} placeholder="Click a data pill below to insert a placeholder, then write your prompt around them..." style={{ ...inputStyle, resize: 'vertical', minHeight: '200px', fontFamily: 'monospace', fontSize: '0.68rem' }} />
                <div style={{ fontSize: '0.55rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', marginBottom: '4px' }}>Click to insert at cursor:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {PLACEHOLDER_PILLS.map(p => {
                    const isUsed = (editing.user_prompt || '').includes(`{{${p.key}}}`);
                    return (
                      <button key={p.key} onClick={() => insertPlaceholder(p.key)}
                        style={{ padding: '3px 9px', fontSize: '0.6rem', fontWeight: 700, borderRadius: '12px', cursor: 'pointer', border: `1px solid ${p.color}${isUsed ? '' : '40'}`, background: isUsed ? `${p.color}20` : 'transparent', color: isUsed ? p.color : theme.textSub, transition: '0.15s', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        {isUsed && <Icon icon={Check} size={9} />}
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => savePrompt(editing)} style={{ flex: 1, padding: '9px', background: accent, color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontSize: '0.72rem' }}>
                    <Icon icon={Save} size={13} /> Save
                  </button>
                  <Button theme={theme} variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </div>
            ) : selectedPrompt ? (
              /* ── Generate mode ── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: theme.text, marginBottom: '2px' }}>{selectedPrompt.name}</div>
                  <div style={{ fontSize: '0.68rem', color: theme.textSub }}>{selectedPrompt.description}</div>
                </div>

                <div style={{ background: theme.card, borderRadius: '8px', padding: '10px', border: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', marginBottom: '6px' }}>Subject</div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.text }}>{subjectName || root || 'No subject selected'}</div>
                </div>

                {/* Dynamic params */}
                {extractParams(selectedPrompt.user_prompt).length > 0 && (
                  <div style={{ background: theme.card, borderRadius: '8px', padding: '10px', border: `1px solid ${theme.border}` }}>
                    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', marginBottom: '6px' }}>Parameters</div>
                    {extractParams(selectedPrompt.user_prompt).map(param => (
                      <div key={param} style={{ marginBottom: '5px' }}>
                        <label style={{ fontSize: '0.62rem', fontWeight: 600, color: theme.textSub, textTransform: 'capitalize' }}>{param.replace(/_/g, ' ')}</label>
                        <Input theme={theme} size="sm" value={genParams[param] || ''} onChange={e => setGenParams(p => ({ ...p, [param]: e.target.value }))} placeholder={param} />
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={runGenerate} disabled={generating || !root}
                  style={{ padding: '11px', background: (generating || !root) ? theme.card : accent, color: (generating || !root) ? theme.textSub : '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: generating ? 'wait' : (!root ? 'not-allowed' : 'pointer'), fontSize: '0.78rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', opacity: (generating || !root) ? 0.6 : 1 }}>
                  {generating ? <><Icon icon={Loader} size={14} /> Generating...</> : <><Icon icon={Zap} size={14} /> Generate</>}
                </button>

                {/* Output */}
                {genResult && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase' }}>Output</div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.55rem', color: theme.textSub }}>{genResult.tokens} tokens · ${genResult.cost_usd?.toFixed(4)}</span>
                        <Button theme={theme} variant="secondary" size="sm" icon={Copy} onClick={copyOutput} title="Copy">Copy</Button>
                        <Button theme={theme} variant="secondary" size="sm" icon={Download} onClick={downloadOutput} title="Download">.md</Button>
                      </div>
                    </div>
                    <div style={{ background: theme.card, borderRadius: '8px', padding: '14px', border: `1px solid ${theme.border}`, maxHeight: '500px', overflowY: 'auto', fontSize: '0.78rem', lineHeight: 1.7, color: theme.text, whiteSpace: 'pre-wrap', fontFamily: "'Georgia', serif" }}>
                      {genResult.output}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ── Empty state ── */
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: '10px', color: theme.textSub }}>
                <Icon icon={BookOpen} size={36} />
                <div style={{ fontSize: '0.78rem' }}>Select a prompt from the library to generate content</div>
              </div>
            )}

          </div>
          </>
          )}
        </div>
      </div>
      <div style={{ flex: 1 }} />
    </div>
  );
}
